import React from 'react';
import { ThumbsUp, MessageCircleQuestion, Loader2 } from 'lucide-react';
import { auditReactions } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Inline thumbs-up + query-flag reactions for audit-trail rows. Partners can
 * quickly acknowledge (thumbs) or flag an entry for follow-up (query with an
 * optional note) without leaving the timeline.
 *
 * Props:
 *   - eventId (required)
 *   - counts  { up, query, me: ['up' | 'query'], queries: [{user,note,at}] }
 *   - onChange(newCounts)
 */
export default function AuditReactions({ eventId, counts, onChange }) {
  const c = counts || { up: 0, query: 0, me: [] };
  const [busy, setBusy] = React.useState(null); // 'up' | 'query'
  const [showQueryInput, setShowQueryInput] = React.useState(false);
  const [note, setNote] = React.useState('');

  const iReacted = (kind) => (c.me || []).includes(kind);

  const doReact = async (kind, extraNote) => {
    setBusy(kind);
    try {
      await auditReactions.react(eventId, kind, extraNote || undefined);
      // Reload counts for this event
      const fresh = await auditReactions.bulk([eventId]);
      onChange?.(fresh.by_event?.[eventId] || { up: 0, query: 0, me: [], queries: [] });
    } catch {
      toast.error('Could not update reaction');
    } finally {
      setBusy(null);
      setShowQueryInput(false);
      setNote('');
    }
  };

  const onQueryClick = () => {
    if (iReacted('query')) return doReact('query');
    setShowQueryInput((s) => !s);
  };

  return (
    <div className="mt-2 flex items-start gap-2" data-testid={`audit-reactions-${eventId}`}>
      <button
        type="button"
        onClick={() => doReact('up')}
        disabled={busy === 'up'}
        title={iReacted('up') ? 'You reacted 👍 — click to remove' : 'Give a thumbs up'}
        data-testid={`audit-react-up-${eventId}`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
          iReacted('up')
            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
        }`}
      >
        {busy === 'up' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
        <span>{c.up || 0}</span>
      </button>
      <button
        type="button"
        onClick={onQueryClick}
        disabled={busy === 'query'}
        title={iReacted('query') ? 'You flagged this — click to remove' : 'Flag with a query'}
        data-testid={`audit-react-query-${eventId}`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
          iReacted('query')
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
        }`}
      >
        {busy === 'query' ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircleQuestion className="h-3 w-3" />}
        <span>{c.query || 0}</span>
      </button>

      {showQueryInput && !iReacted('query') && (
        <div className="flex flex-1 items-center gap-1">
          <input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note explaining the query…"
            maxLength={500}
            data-testid={`audit-react-query-note-${eventId}`}
            className="h-6 flex-1 rounded-md border border-amber-200 bg-amber-50/40 px-2 text-[11px] focus:outline-none focus:ring-2 focus:ring-amber-200"
            onKeyDown={(e) => e.key === 'Enter' && doReact('query', note)}
          />
          <button
            type="button"
            data-testid={`audit-react-query-save-${eventId}`}
            onClick={() => doReact('query', note)}
            className="rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-amber-600"
          >
            Flag
          </button>
        </div>
      )}

      {c.queries?.length > 0 && !showQueryInput && (
        <div className="ml-2 flex-1 text-[10px] italic text-amber-700" data-testid={`audit-queries-${eventId}`}>
          {c.queries[0].user}: “{c.queries[0].note}”
          {c.queries.length > 1 && <span className="text-slate-400"> +{c.queries.length - 1} more</span>}
        </div>
      )}
    </div>
  );
}
